import React from "react";
import {
  Loader,
  LoaderSizes,
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import axios from "axios";
import { useNavigate } from "react-router";
import "./FeatureComparison.scss";

interface FeatureData {
  id: string;
  featureName: string;
  techID?: string;
  status?: string;
  notes?: string;
  android?: number;
  ios?: number;
  category?: string;
  subCategory?: string;
  type?: string;
  apiVersion?: string;
  availability?: string;
  hidResponsibility?: string;
  [key: string]: any; // For dynamic technology columns
}

interface Technology {
  technologyID: string;
  techID?: string;
  name?: string;
  type: string;
  description?: string;
  hidResponsibility?: string;
  apiVersion?: string;
  availability?: string;
  psTrainingStatus?: string;
}

interface GroupedFeature {
  categoryName: string;
  categoryKey: string;
  subCategories: {
    [key: string]: {
      subCategoryName: string;
      features: FeatureData[];
    };
  };
}

const FeatureComparison = () => {
  const [features, setFeatures] = React.useState<FeatureData[]>([]);
  const [groupedFeatures, setGroupedFeatures] = React.useState<
    GroupedFeature[]
  >([]);
  const [technologies, setTechnologies] = React.useState<Technology[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [expandedCategories, setExpandedCategories] = React.useState<{
    [key: string]: boolean;
  }>({});
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [selectedCategory, setSelectedCategory] = React.useState<string>("all");
  const navigate = useNavigate();

  React.useEffect(() => {
    // Fetch data from APIs on component mount
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch both APIs in parallel
      const [technologiesResponse, featuresResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/technologies`
        ),
        axios.get(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-comparison`
        ),
      ]);

      // Process technologies data
      let techList: Technology[] = [];
      if (technologiesResponse.data) {
        const techData =
          technologiesResponse.data.data || technologiesResponse.data;
        const allTech = Array.isArray(techData) ? techData : [];
        // Filter out technologies with type "N/A"
        techList = allTech.filter((tech) => tech.type && tech.type !== "N/A");
        setTechnologies(techList);
      }

      // Process feature comparison data
      if (featuresResponse.data) {
        const featData = featuresResponse.data.data || featuresResponse.data;
        const featuresArray = Array.isArray(featData) ? featData : [];
        setFeatures(featuresArray);

        // Group and structure the data
        const grouped = groupFeaturesByCategory(featuresArray, techList);
        setGroupedFeatures(grouped);

        // Auto-expand first category by default
        if (grouped.length > 0) {
          setExpandedCategories({ [grouped[0].categoryKey]: true });
        }
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);

      if (error.response) {
        console.error("Response error:", error.response.data);
      } else if (error.request) {
        console.error("Network error: No response from server");
      }
    } finally {
      setLoading(false);
    }
  };

  const groupFeaturesByCategory = (
    featuresData: FeatureData[],
    techList: Technology[]
  ): GroupedFeature[] => {
    const categoryMap: { [key: string]: GroupedFeature } = {};

    // Group features by category and subcategory
    featuresData.forEach((feature) => {
      const categoryKey = feature.category || "other";
      const categoryName = formatCategoryName(categoryKey);
      const subCategoryKey = feature.subCategory || "uncategorized";
      const subCategoryName = formatSubCategoryName(subCategoryKey);

      // Initialize category if not exists
      if (!categoryMap[categoryKey]) {
        categoryMap[categoryKey] = {
          categoryName,
          categoryKey,
          subCategories: {},
        };
      }

      // Initialize subcategory if not exists
      if (!categoryMap[categoryKey].subCategories[subCategoryKey]) {
        categoryMap[categoryKey].subCategories[subCategoryKey] = {
          subCategoryName,
          features: [],
        };
      }

      // Add feature to subcategory
      categoryMap[categoryKey].subCategories[subCategoryKey].features.push(
        feature
      );
    });

    return Object.values(categoryMap);
  };

  const formatCategoryName = (category: string): string => {
    const categoryNames: { [key: string]: string } = {
      apiFeatures: "API Features",
      sdkFeature: "SDK Features",
      generalFeatures: "General Features",
    };
    return categoryNames[category] || category;
  };

  const formatSubCategoryName = (subCategory: string): string => {
    const subCategoryNames: { [key: string]: string } = {
      issuanceTokenManagement: "Issuance Token Management",
      userManagement: "User Management",
      partnumberCredentialTemplate: "Partnumber/Credential Template",
      bleSupport: "BLE Support",
    };
    return subCategoryNames[subCategory] || subCategory;
  };

  const cleanCategoryName = (name: string): string => {
    return name.replace(/uncategorized/gi, "").trim();
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const expandAllCategories = () => {
    const allExpanded: { [key: string]: boolean } = {};
    groupedFeatures.forEach((cat) => {
      allExpanded[cat.categoryKey] = true;
    });
    setExpandedCategories(allExpanded);
  };

  const collapseAllCategories = () => {
    setExpandedCategories({});
  };

  const filterGroupedFeatures = (): GroupedFeature[] => {
    let filtered = groupedFeatures;

    // Filter by selected category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((cat) => cat.categoryKey === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered
        .map((category) => {
          const filteredSubCategories: typeof category.subCategories = {};

          Object.entries(category.subCategories).forEach(
            ([subCatKey, subCatData]) => {
              const filteredFeatures = subCatData.features.filter(
                (feat) =>
                  feat.featureName?.toLowerCase().includes(searchLower) ||
                  feat.notes?.toLowerCase().includes(searchLower) ||
                  feat.status?.toLowerCase().includes(searchLower)
              );

              if (filteredFeatures.length > 0) {
                filteredSubCategories[subCatKey] = {
                  ...subCatData,
                  features: filteredFeatures,
                };
              }
            }
          );

          return {
            ...category,
            subCategories: filteredSubCategories,
          };
        })
        .filter((cat) => Object.keys(cat.subCategories).length > 0);
    }

    return filtered;
  };

  const fetchFeatureComparison = async () => {
    try {
      setLoading(true);
      await fetchAllData();
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSupportedStatus = (value: string) => {
    if (!value) return <span className="status-empty">-</span>;

    const lowerValue = value.toLowerCase();

    if (lowerValue.includes("supported") && !lowerValue.includes("not")) {
      return <span className="status-supported">{value}</span>;
    } else if (
      lowerValue.includes("not supported") ||
      lowerValue.includes("not applicable")
    ) {
      return <span className="status-not-supported">{value}</span>;
    } else if (lowerValue.includes("tbd")) {
      return <span className="status-tbd">{value}</span>;
    } else {
      return <span className="status-other">{value}</span>;
    }
  };

  return (
    <div className="feature-comparison-container">
      <div className="header-container">
        <div className="header-content">
          <div className="left">
            <h1 className="page-title">Feature Comparison Table</h1>
            <p className="page-subtitle">
              Compare features across different platforms
            </p>
          </div>
          <div className="right">
            {/* <Button
              variant="secondary"
              label="Back to Incident List"
              icon={
                <Iconography icon="arrowLeft" size={IconographySizes.Medium} />
              }
              onClick={() =>
                navigate("/hid-partner-admin/dashboard/incident-list")
              }
            /> */}
            <Button
              variant="primary"
              label="Refresh"
              icon={
                <Iconography
                  icon="arrowsRotate"
                  size={IconographySizes.Medium}
                />
              }
              onClick={fetchFeatureComparison}
            />
          </div>
        </div>
      </div>

      {/* Filter and Control Bar */}
      <div className="controls-bar">
        <div className="controls-left">
          <div className="search-wrapper">
            <input
              type="text"
              placeholder="Search features..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm("")}
              >
                ✕
              </button>
            )}
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            <option value="all">All Categories</option>
            {groupedFeatures.map((cat) => (
              <option key={cat.categoryKey} value={cat.categoryKey}>
                {cat.categoryName}
              </option>
            ))}
          </select>
        </div>

        <div className="controls-right">
          <Button
            variant="secondary"
            label="Expand All"
            size="small"
            onClick={expandAllCategories}
            icon={
              <Iconography icon="angleDown" size={IconographySizes.Small} />
            }
          />
          <Button
            variant="secondary"
            label="Collapse All"
            size="small"
            onClick={collapseAllCategories}
            icon={<Iconography icon="angleUp" size={IconographySizes.Small} />}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <Loader variant={LoaderSizes.large} />
          <p>Loading feature comparison...</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <div className="table-scroll-container">
            <table className="feature-comparison-table">
              <thead>
                <tr>
                  <th className="feature-column sticky-column">Feature</th>
                  {technologies.length > 0 ? (
                    technologies.map((tech) => {
                      const tooltipText = `Type: ${
                        tech.type
                      }\nHID Responsibility: ${
                        tech.hidResponsibility || "N/A"
                      }\nAPI Version: ${
                        tech.apiVersion || "N/A"
                      }\nAvailability: ${
                        tech.availability || "N/A"
                      }\nPS Training Status: ${tech.psTrainingStatus || "N/A"}`;
                      return (
                        <th
                          key={tech.techID || tech.technologyID}
                          title={tooltipText}
                        >
                          {tech.type || tech.name}
                        </th>
                      );
                    })
                  ) : (
                    <th>No Technologies Available</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {groupedFeatures.length === 0 && features.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        technologies.length > 0 ? technologies.length + 1 : 2
                      }
                      className="no-data"
                    >
                      <div className="no-data-content">
                        <Iconography
                          icon="fileCircleExclamation"
                          size={IconographySizes.XXLarge}
                        />
                        <h3>No Feature Comparison Data</h3>
                        <p>
                          No feature comparison data available. Please try
                          refreshing or check back later.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : filterGroupedFeatures().length > 0 ? (
                  <>
                    {filterGroupedFeatures().map((category, catIndex) => {
                      const isExpanded =
                        expandedCategories[category.categoryKey];
                      const subCategoriesCount = Object.keys(
                        category.subCategories
                      ).length;

                      return (
                        <React.Fragment key={category.categoryKey}>
                          {/* Category Header Row - Collapsible */}
                          <tr
                            className="category-row-clickable"
                            onClick={() => toggleCategory(category.categoryKey)}
                          >
                            <td
                              className="feature-name sticky-column category-header"
                              colSpan={technologies.length + 1}
                            >
                              <div className="category-header-content">
                                <Iconography
                                  icon={isExpanded ? "angleDown" : "angleRight"}
                                  size={IconographySizes.Small}
                                />
                                <span>
                                  {catIndex + 1}. {category.categoryName}
                                </span>
                                <span className="category-count">
                                  ({subCategoriesCount}{" "}
                                  {subCategoriesCount === 1
                                    ? "subcategory"
                                    : "subcategories"}
                                  )
                                </span>
                              </div>
                            </td>
                          </tr>

                          {/* Subcategories and Features - Only show if expanded */}
                          {isExpanded &&
                            Object.entries(category.subCategories).map(
                              ([subCatKey, subCatData], subCatIndex) => {
                                const subCatNumber = `${catIndex + 1}.${
                                  subCatIndex + 1
                                }`;

                                // Group features by featureName to show each feature once with all tech values
                                const featuresByName: {
                                  [key: string]: FeatureData[];
                                } = {};
                                subCatData.features.forEach((feat) => {
                                  const name = feat.featureName || "Unknown";
                                  if (!featuresByName[name]) {
                                    featuresByName[name] = [];
                                  }
                                  featuresByName[name].push(feat);
                                });

                                const cleanedSubCatName = cleanCategoryName(
                                  subCatData.subCategoryName
                                );

                                return (
                                  <React.Fragment key={subCatKey}>
                                    {/* Subcategory Header Row - Only show if name is not empty */}
                                    {cleanedSubCatName &&
                                      cleanedSubCatName !== "-" && (
                                        <tr>
                                          <td
                                            className="feature-name sticky-column section-header"
                                            colSpan={technologies.length + 1}
                                          >
                                            {subCatNumber} {cleanedSubCatName}
                                          </td>
                                        </tr>
                                      )}

                                    {/* Feature Rows */}
                                    {Object.entries(featuresByName).map(
                                      (
                                        [featureName, featuresList],
                                        featIndex
                                      ) => {
                                        const featureNumber = `${subCatNumber}.${
                                          featIndex + 1
                                        }`;

                                        return (
                                          <tr key={`${subCatKey}-${featIndex}`}>
                                            <td className="feature-name sticky-column sub-feature">
                                              {featureNumber} {featureName}
                                            </td>
                                            {technologies.map((tech) => {
                                              // Find matching feature for this technology using techID
                                              const techIDToMatch =
                                                tech.techID ||
                                                tech.technologyID;
                                              const matchingFeature =
                                                featuresList.find(
                                                  (f) =>
                                                    f.techID === techIDToMatch
                                                );
                                              const value = matchingFeature
                                                ? matchingFeature.notes ||
                                                  matchingFeature.status ||
                                                  "-"
                                                : "-";

                                              return (
                                                <td
                                                  key={
                                                    tech.techID ||
                                                    tech.technologyID
                                                  }
                                                >
                                                  {getSupportedStatus(value)}
                                                </td>
                                              );
                                            })}
                                          </tr>
                                        );
                                      }
                                    )}
                                  </React.Fragment>
                                );
                              }
                            )}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan={
                        technologies.length > 0 ? technologies.length + 1 : 2
                      }
                      className="no-data"
                    >
                      <div className="no-data-content">
                        <Iconography
                          icon="magnifyingGlass"
                          size={IconographySizes.XXLarge}
                        />
                        <h3>No Results Found</h3>
                        <p>
                          No features match your current search or filter
                          criteria.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureComparison;
